/**
 * @file Layout du dashboard : un seul arbre, le réglage de menu ne change que le desktop.
 * @module packages.common.layouts.app.layout
 *
 * Sous `lg`, tous les supports partagent le même squelette : bannière admin,
 * header mobile avec hamburger, tiroir de navigation, contenu, barre du bas.
 * Le réglage `themeLayout` (vertical, mini, horizontal) ne choisit que le menu
 * affiché à partir de `lg` et le sens du cadre (colonne latérale ou barre en
 * haut). Une seule source de vérité : plus de branche par réglage qui
 * recopierait le squelette et oublierait un morceau.
 */

import PropTypes from 'prop-types';
// @mui
import Box from '@mui/material/Box';
import GlobalStyles from '@mui/material/GlobalStyles';
// hooks
import {useBoolean} from 'packages/common/hooks/useBoolean';
import {useResponsive} from 'packages/common/hooks/useResponsive';
// components
import {useSettingsContext} from 'packages/common/components/settings';
import {AdminAccessProvider} from 'packages/common/contexts/admin-access-context';
import {AdminAccessBanner} from 'packages/common/components/admin-access-banner';
//
import Main from './main';
import NavMini from './nav-mini';
import NavVertical from './nav-vertical';
import NavHorizontal from './nav-horizontal';
import MobileHeader from './mobile-header';
import BottomNav from './bottom-nav';

// ----------------------------------------------------------------------

// Styles globaux pour l'app dashboard - Force le fond gris sur le body
const appGlobalStyles = (
  <GlobalStyles
    styles={(theme) => ({
      body: {
        backgroundColor: theme.palette.grey[100],
      },
    })}
  />
);

/**
 * Menu desktop selon le réglage (le tiroir mobile est commun à tous).
 *
 * @param {string} themeLayout - Réglage `vertical` | `mini` | `horizontal`
 * @return {JSX.Element} Menu à afficher à partir de `lg`
 */
function renderDesktopNav(themeLayout) {
  if (themeLayout === 'horizontal') return <NavHorizontal />;
  if (themeLayout === 'mini') return <NavMini />;
  return null;
}

export default function DashboardLayout({children, fullHeight}) {
  const settings = useSettingsContext();

  const lgUp = useResponsive('up', 'lg');

  const nav = useBoolean();

  const isHorizontal = settings.themeLayout === 'horizontal';

  // Le tiroir mobile et le menu vertical desktop sont le même composant : il
  // sait lui-même se rendre en tiroir sous `lg` et en colonne au-dessus.
  const renderNavVertical = <NavVertical openNav={nav.value} onCloseNav={nav.onFalse} onOpenNav={nav.onTrue} />;
  const desktopNav = renderDesktopNav(settings.themeLayout);

  return (
    <AdminAccessProvider>
      {appGlobalStyles}
      <AdminAccessBanner />

      {/* Header mobile : sous lg, quel que soit le réglage de menu desktop */}
      {!lgUp && <MobileHeader onOpenNav={nav.onTrue} />}

      <Box
        sx={{
          'minHeight': fullHeight ? undefined : '100vh',
          // Utilise dvh (dynamic viewport height) pour gérer les barres d'adresse mobiles
          // Fallback sur 100vh pour les navigateurs sans support dvh
          'height': fullHeight ? '100dvh' : undefined,
          // Fallback iOS Safari
          '@supports (-webkit-touch-callout: none)': {
            height: fullHeight ? '-webkit-fill-available' : undefined,
          },
          'display': 'flex',
          // Colonne latérale à partir de lg, sauf menu horizontal (barre en haut)
          'flexDirection': {xs: 'column', lg: isHorizontal ? 'column' : 'row'},
          'bgcolor': 'grey.100',
        }}
      >
        {lgUp && desktopNav ? desktopNav : renderNavVertical}

        <Main fullHeight={fullHeight}>{children}</Main>
      </Box>

      {/* Bottom nav mobile */}
      <BottomNav />
    </AdminAccessProvider>
  );
}

DashboardLayout.propTypes = {
  children: PropTypes.node,
  fullHeight: PropTypes.bool,
};
